import {
  PROJECTS_SKILL_NODE_NAME,
  type ProjectsComposerSkillReference,
} from '@heidi/agents/data';
import { cn } from '@heidi/utils';
import { mergeAttributes, Node } from '@tiptap/core';
import {
  NodeViewWrapper,
  ReactNodeViewRenderer,
  type NodeViewProps,
} from '@tiptap/react';

import type { JSX } from 'react';

export type ProjectsSkillReferenceAttrs = ProjectsComposerSkillReference;

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    projectsSkillReference: {
      insertProjectsSkillReference: (attrs: ProjectsSkillReferenceAttrs) => ReturnType;
    };
  }
}

export const ProjectsSkillReferenceNode = Node.create({
  name: PROJECTS_SKILL_NODE_NAME,
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      skillId: {
        default: '',
        parseHTML: (element: HTMLElement) => element.dataset.skillId ?? '',
        renderHTML: (attrs: { skillId?: string }) => ({
          'data-skill-id': attrs.skillId ?? '',
        }),
      },
      recordingId: {
        default: undefined,
        parseHTML: (element: HTMLElement) => element.dataset.recordingId ?? undefined,
        renderHTML: (attrs: { recordingId?: string }) =>
          attrs.recordingId ? { 'data-recording-id': attrs.recordingId } : {},
      },
      slug: {
        default: '',
        parseHTML: (element: HTMLElement) => element.dataset.skillSlug ?? '',
        renderHTML: (attrs: { slug?: string }) => ({
          'data-skill-slug': attrs.slug ?? '',
        }),
      },
      title: {
        default: '',
        parseHTML: (element: HTMLElement) => element.dataset.skillTitle ?? '',
        renderHTML: (attrs: { title?: string }) => ({
          'data-skill-title': attrs.title ?? '',
        }),
      },
      kind: {
        default: undefined,
        parseHTML: (element: HTMLElement) => element.dataset.skillKind ?? undefined,
        renderHTML: (attrs: { kind?: string }) =>
          attrs.kind ? { 'data-skill-kind': attrs.kind } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-projects-skill-reference]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { 'data-projects-skill-reference': '' }),
      HTMLAttributes['data-skill-slug'] ?? '',
    ];
  },

  addCommands() {
    return {
      insertProjectsSkillReference:
        (attrs) =>
        ({ state, tr }) => {
          const skillType = state.schema.nodes[this.name];
          if (!skillType) return false;

          tr.replaceSelectionWith(skillType.create(attrs), false);
          tr.insertText(' ');
          return true;
        },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ProjectsSkillReferenceNodeView);
  },
});

const ROUTINE_TOKEN_CLASS =
  'inline-flex min-w-0 max-w-full rounded-full bg-entity-purple-muted/50 px-1 align-baseline transition-colors duration-200 ease-out hover:bg-entity-purple-muted';

function ProjectsSkillReferenceNodeView({ node, selected }: NodeViewProps): JSX.Element {
  const attrs = node.attrs as ProjectsSkillReferenceAttrs;
  const label = getSkillDisplayLabel(attrs);

  return (
    <NodeViewWrapper
      as="span"
      data-testid="projects-composer-inline-skill"
      data-skill-slug={attrs.slug}
      className={cn(ROUTINE_TOKEN_CLASS, selected && 'bg-entity-purple-muted')}
      contentEditable={false}
    >
      <span className="min-w-0 truncate" title={label}>
        {label}
      </span>
    </NodeViewWrapper>
  );
}

function getSkillDisplayLabel(attrs: ProjectsSkillReferenceAttrs): string {
  const slug = attrs.slug.trim();
  if (slug.length > 0) return `/${slug}`;

  const title = attrs.title.trim();
  return title.length > 0 ? title : 'Skill';
}
